/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
import { useSnackbar } from 'notistack';
import { FC, useEffect, useMemo, useState } from 'react';
import { useAuthenticated } from '../../auth';
import { useConfig, useScript } from '../../hooks';
import { ErrorPage } from '../../pages';
import { Nullable, Undefinable } from '../../types';
import { addLocalStorageItem, CodedError, fetchWatToken, getLocalStorageItem, getSisenseLanguageCode } from '../../util';
import { useBoolFlag, useNumberFlag } from '../flags';
import { MiddleSpinner } from '../middle-spinner';
import { EventArguments, SisenseFrameConstructor } from './types';
import { DateTime } from 'luxon';
import { setTimezone } from './sisense-sync-timezone';

type DashboardType = 'dashboards' | 'overview' | 'reports' | 'analytics';

enum FilteringType {
  All = 1,
  Specific = 2
}

interface Props {
  dashboardId: string;
  type: DashboardType;
}

interface DashboardProps extends Props {
  sisenseUrl: string;
  wat: string;
  theme?: string;
}

interface PersistingFilters {
  filters: Nullable<string>;
  timeStamp: number;
}

interface FilterObject {
  disabled: boolean;
  isCascading: boolean;
  jaql?: JaqlObject;
  levels?: JaqlObject[];
}

interface JaqlObject {
  title: string;
  dim: string;
  datatype: string;
  filter: {
    startsWith?: string;
  };
}

declare global {
  interface Window {
    'sisense.embed': Undefinable<{ SisenseFrame: SisenseFrameConstructor }>;
  }
}

const setPersistentFilters = (dashboardId: string, dashboardType: DashboardType, filters: PersistingFilters, filterType: FilteringType): void => {
  const key = filterType === FilteringType.All ? dashboardType : dashboardId;
  const currentStorage = getLocalStorageItem<Record<string, PersistingFilters>>('persisting-filters') || {};
  currentStorage[key] = filters;
  addLocalStorageItem('persisting-filters', currentStorage);
};

const getPersistentFilters = (dashboardId: string, dashboardType: DashboardType, filterType: FilteringType): Nullable<PersistingFilters> => {
  const key = filterType === FilteringType.All ? dashboardType : dashboardId;
  const currentStorage = getLocalStorageItem<Record<string, PersistingFilters>>('persisting-filters');
  return currentStorage?.[key] || null;
};

export const Dashboard: FC<DashboardProps> = ({ dashboardId, sisenseUrl, wat, theme, type }) => {
  const [iframeRef, setIframeRef] = useState<Nullable<HTMLIFrameElement>>(null);
  const { enqueueSnackbar } = useSnackbar();
  const variationFilter = useNumberFlag('track-dashboard-persisting-variation-filters');
  const v2DashSwitching = useBoolFlag('portals-dashboard-switching-enhancements-temporary-20240918') ?? false;
  const sisenseEmbed = window['sisense.embed'];

  useMemo(() => {
    if (!iframeRef) return undefined;
    if (!sisenseEmbed) {
      console.log("window['sisense.embed'] is undefined");
      return undefined;
    }

    const { SisenseFrame } = sisenseEmbed;
    const sisenseFrame = new SisenseFrame({
      // Sisense application URL, including protocol and port if required
      url: sisenseUrl,
      // Web access token:
      wat,
      // OID of dashboard to load initially
      dashboard: dashboardId,
      // Which panels to show in the iFrame
      settings: {
        showLeftPane: false,
        showToolbar: true,
        showRightPane: true,
      },
      element: iframeRef,
      theme,
      language: getSisenseLanguageCode(),
    });
    sisenseFrame.render().catch((err: Error) => {
      enqueueSnackbar(err.message, { variant: 'error' });
    });

    sisenseFrame.render().then(() => {
      if (!variationFilter) { return; }
      let initialLoad = true;

      const saveDashboard = (args: EventArguments): void => {
        if (!v2DashSwitching) { return; }

        let dashId;
        if (type === 'dashboards') {
          dashId = { dashboards: args.dashboard.oid };
        }
        else if (type === 'reports') {
          dashId = { reports: args.dashboard.oid };
        }
        else {
          dashId = { analytics: args.dashboard.oid };
        }
        addLocalStorageItem('current-dashboards', dashId);
      };

      const induceFilterChange = (args: EventArguments): void => {
        if (initialLoad) {
          // Detects if the dashboard filters has a fromDate instance
          const firstMatchFromDate = args.dashboard.filters.find((f) => f.jaql?.datatype === 'datetime' && f.jaql?.filter && Object.prototype.hasOwnProperty.call(f.jaql.filter, 'last'));
          if (!firstMatchFromDate) {
            sisenseFrame.dashboard.applyFilters(args.dashboard.filters, false).catch(console.error);
            return;
          }
        }
      };

      // Returns if a particular filter exists in an array of filters with a particular dim / set of dims
      const filterExistsInSaved = (currentFilter: FilterObject, savedFilters: FilterObject[]): boolean => {
        if (!currentFilter.jaql) {
          return currentFilter.levels?.some((currentLevel) => {
            return savedFilters.some((savedFilter) => {
              return savedFilter.levels?.some((savedLevel) => savedLevel.dim === currentLevel.dim);
            });
          }) ?? false;
        }
        return savedFilters.some((savedFilter) => savedFilter.jaql?.dim === currentFilter.jaql?.dim);
      };

      // Handles changes when the filter change event is called
      const handleDashboardFiltersChanged = (args: EventArguments): void => {
        const savedFilters = getPersistentFilters(dashboardId, type, variationFilter);
        const currentFilters = args.dashboard.filters;

        if (initialLoad) {
          initialLoad = false;
          if (savedFilters?.filters) {
            const decodedFilters: FilterObject[] = JSON.parse(atob(savedFilters.filters)) as FilterObject[];
            const matchingFilters: FilterObject[] = currentFilters
              .map((currentFilter: FilterObject) =>
                decodedFilters.find((decodedFilter: FilterObject) => filterExistsInSaved(currentFilter, [decodedFilter])),
              ).filter((filter): filter is FilterObject => Boolean(filter));

            const filterAge = Date.now() - savedFilters.timeStamp;
            const isExpired = filterAge >= 24 * 60 * 60 * 1000;
            if (!isExpired) {
              sisenseFrame.dashboard.clearFilters(true).catch(console.error);
              sisenseFrame.dashboard.applyFilters(matchingFilters, true).catch(console.error);
            } else {
              setPersistentFilters(dashboardId, type, { filters: null, timeStamp: Date.now() }, variationFilter);
            }
          }
          return;
        }

        const filterObjectArray: FilterObject[] = savedFilters?.filters
          ? JSON.parse(atob(savedFilters.filters)) as FilterObject[]
          : [];

        const combinedFilters: FilterObject[] = currentFilters.reduce((acc: FilterObject[], currentFilter: FilterObject) => {
          const matchingIndex = filterObjectArray.findIndex(
            (f: FilterObject) => f.jaql?.dim === currentFilter.jaql?.dim ||
              (f.levels && currentFilter.levels &&
                f.levels.some((level) => currentFilter.levels?.some((cl) => cl.dim === level.dim))
              ),
          );

          if (matchingIndex !== -1) {
            // If a matching filter is found, replace the saved filter with the current one
            acc[matchingIndex] = currentFilter;
          } else {
            // If no match is found, add the current filter to the accumulated filters
            acc.push(currentFilter);
          }

          return acc;
        }, [...filterObjectArray]); // Start with a copy of filterObjectArray

        const filtersChanged = JSON.stringify(currentFilters) !== JSON.stringify(filterObjectArray);
        if (filtersChanged) {
          setPersistentFilters(dashboardId, type, { filters: btoa(JSON.stringify(combinedFilters)), timeStamp: Date.now() }, variationFilter);
        }
      };

      sisenseFrame.dashboard.on('dashboardfilterschanged', handleDashboardFiltersChanged);
      sisenseFrame.dashboard.on('dashboardloaded', induceFilterChange);
      sisenseFrame.dashboard.on('dashboardloaded', saveDashboard);
    }).catch((err: Error) => {
      enqueueSnackbar(err.message, { variant: 'error' });
    });
  }, [dashboardId, iframeRef, sisenseEmbed, wat]);

  return <iframe ref={setIframeRef} style={{ border: 'none', height: '100%' }} />;
};

export const SisenseDashboard: FC<Props> = ({ dashboardId, type }) => {
  const config = useConfig() as { sisense: { baseUrl: string; theme?: string } };
  const sisenseUrl = config?.sisense?.baseUrl;
  const sisenseTheme = config?.sisense?.theme;
  const { axios } = useAuthenticated();
  const { restClient } = useAuthenticated();
  const [wat, setWat] = useState<string>();
  const [error, setError] = useState<Error>();
  if (!sisenseUrl) return <ErrorPage error={new CodedError('Reporting is not configured', 'error-sisense-not-configured')} />;

  const scriptUrl = `${sisenseUrl}/js/frame.js`;
  const state = useScript(scriptUrl);

  useEffect(() => {
    if (wat) {
      return;
    }

    fetchWatToken(restClient).then(({ wat }) => {
      setWat(wat);
    }).catch((err: Error) => {
      setError(err);
    });
  }, [restClient]);

  useEffect(() => {
    const existingTz = localStorage.getItem('sisense-tz');
    const tzIana = DateTime.local().zoneName;

    if (existingTz === tzIana){
      return;
    }

    setTimezone(tzIana, axios)
      .then(() => {
        localStorage.setItem('sisense-tz', tzIana);
      })
      .catch((err: Error) => {
        console.error(err);
      });
  }, [axios]);

  if (state.error) {
    return <ErrorPage error={new CodedError('Unable to load report', 'error-loading-analytics-scripts')} />;
  }

  if (error) return <ErrorPage error={error} />;
  if (!wat) return <MiddleSpinner />;

  return <Dashboard dashboardId={dashboardId} sisenseUrl={sisenseUrl} wat={wat} theme={sisenseTheme} type={type} />;
};
